import VoltError from 'VoltError'
import GenericFetch from 'framework/genericfetch'
import { of, throwError } from 'rxjs'
import { mergeMap, catchError } from 'rxjs/operators'
import { ERROR_BACKEND_MAPPING } from './errors'

/**
 * Fetching API class.
 */
export default class Fetch extends GenericFetch {
    constructor(config, otherApis = {}) {
        super(config, 'ufinity')
        this.registerErrors(ERROR_BACKEND_MAPPING)
    }

    get serverTimeStampOffset() {
        return this._serverTimeStampOffset
    }

    set serverTimeStampOffset(serverDateTime) {
        let _date
        if (serverDateTime instanceof Date) {
            _date = serverDateTime
        } else if (typeof serverDateTime === 'string') {
            _date = new Date(serverDateTime)
        }

        if (_date && !isNaN(_date.getTime())) {
            /*const JANUARY_01_2023 = 1672560000000 // Line code written in 03/02/2023 as a security we are necessary after
            const timestamp = _date.getTime()
            if (timestamp >= JANUARY_01_2023) {
                this._serverTimeStampOffset = Date.now() - timestamp
            }*/
            this._serverTimeStampOffset = Date.now() - _date.getTime()
        }
    }

    /**
     * Fetching Method call with ajax API. Output of the function is then automatically parsed
     *
     * @override added serverTimeStampOffset on every request
     *
     * @param {Object} args request arguments
     * @param {String} args.url
     * @param {Object} args.body : HTTP URL
     * @param {Object} args.headers : HTTP Header
     * @param {String} args.method : GET, PUT, POST, DELETE, HEAD, OPTIONS
     * @param {Number} args.timeout
     * @param {String} [args.log=''] Extra message for Debug
     * @param {Boolean} [args.retrieveResponseHeaders=false] False by default to same CPU as not always needed
     * @param {Boolean} [args.withCredentials=undefined] Whether or not a CORS request was sent with credentials. If false, will also ignore cookies in the CORS response.
     * @param {VoltError} [args.defaultError=undefined] Default error to return when UNKNOWN_API_ERROR is thrown
     * @returns { response, totalCount }
     */
    fetch(args) {
        return super.fetch({ ...args, retrieveResponseHeaders: true }).pipe(
            mergeMap((result = {}) => {
                if (result.headers && (result.headers.date || result.headers.Date)) {
                    this.serverTimeStampOffset = result.headers.date || result.headers.Date
                }
                return of(result)
            }),
            catchError((errorObject) => {
                if (
                    errorObject.code ===
                        VoltError.codes.UNAUTHORIZED_DUE_TO_SYSTEM_CLOCK_UNSYNCHRONIZED.code &&
                    errorObject.httpResponseHeaders &&
                    (errorObject.httpResponseHeaders.date || errorObject.httpResponseHeaders.Date)
                ) {
                    this.serverTimeStampOffset =
                        errorObject.httpResponseHeaders.date || errorObject.httpResponseHeaders.Date
                }
                return throwError(errorObject)
            })
        )
    }

    /**
     * Recursively executes an api call until all results have been retrieved
     *
     * @param {function} req The function called to fetch elements from the backend
     * @param {Object} opts
     * @param {number} opts.pageSize The number of elements to be retrieved per fetch
     * @param {number} opts.offset The offset of the first element of next backend call
     *
     * @returns {Array<object>} An array of raw elements
     */
    recursiveListFetch(req, opts = {}) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: 'Recursive fetch not yet implemented',
            })
        )
    }

    /**
     * Error extraction. Inherited from fetch
     * @param {Object} response
     * @returns
     */
    extractError(response) {
        if (!response) {
            return { error: 'unknown', description: 'unknown', errorCode: 'unknown' }
        }
        if (response.error) {
            return {
                error: response.error,
                description: response.error_description,
                errorCode: 'unknown',
            }
        }
        // Error response type for Starhub gateway
        if (response.apierror) {
            const { status, message, extErrorCode, subErrors } = response.apierror
            return {
                // Dynamically manage extError presence
                error: extErrorCode ? extErrorCode : status,
                description:
                    subErrors && subErrors.length > 0 && subErrors[0]?.message
                        ? subErrors[0]?.message
                        : message,
                errorCode: extErrorCode,
                subErrors: subErrors,
            }
        }

        return { error: 'unknown', description: 'Error while processing the request' }
    }
}
